import React from 'react'
import { useState, useRef, useMemo } from 'react'
import { Dropdown } from 'antd'
import type { MenuProps } from 'antd'
import styles from './style.module.scss'
import moment, { Moment } from 'moment'
import { useTranslation } from 'react-i18next'

import chevronDownIcon from 'assets/images/chevron-down-24.svg'
import { v4 as uuidv4 } from 'uuid'

interface TimezoneInfo {
  tzId: string | null
  cityName: string
  searchtext: string
}

interface TimezonePickerProps {
  tzId: string | null
  focusDate: Date
  onTzChange: (tzId: string | null) => void
  forSettings?: boolean
  disabled?: boolean
}

const TimezonePicker: React.FC<TimezonePickerProps> = ({
  tzId,
  focusDate,
  onTzChange,
  forSettings = false,
  disabled = false,
}) => {
  const { t } = useTranslation()

  const [searchText, setSearchText] = useState<string>('')
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const tzInputRef = useRef<HTMLInputElement>(null)
  const allTimezoneInfo = useMemo<TimezoneInfo[]>(() => {
    const info = Intl.supportedValuesOf('timeZone').map((tz) => {
      /* i18next-extract-disable-next-line */
      const aCityName = t('timezone.' + tz)
      let cityName = aCityName
      if (aCityName === 'timezone.' + tz) {
        cityName = tz.split('/').pop() || tz
      }
      return {
        tzId: tz,
        cityName: cityName,
        searchtext: tz + ' ' + cityName,
      }
    })
    if (forSettings) {
      info.unshift({
        tzId: null,
        cityName: t('settings.accountTabs.none'),
        searchtext: t('settings.accountTabs.none'),
      })
    }
    return info
  }, [forSettings, t])

  const pickerId = useMemo<string>(() => {
    return uuidv4()
  }, [])

  const getTimezoneOffsetAndName = (tz: string | null, date: Date): string => {
    if (!tz) {
      if (forSettings) {
        return t('settings.accountTabs.none')
      } else {
        tz = 'Etc/UTC'
      }
    }

    const momentTz = moment(date).tz(tz)
    /* i18next-extract-disable-next-line */
    const aCityName = t('timezone.' + tz)
    let cityName = aCityName
    if (aCityName === 'timezone.' + tz) {
      cityName = tz.split('/').pop() || tz
    }

    return `(${getTZOffsetFromMoment(momentTz)}) ${cityName}`
  }

  const getTZOffsetFromMoment = (momentDate: Moment | null): string | null => {
    if (!momentDate) {
      return null
    }
    const offset = momentDate.format('Z')
    const isDST = momentDate.isDST()
    return `UTC${offset}${isDST ? '☀️' : ''}`
  }

  const getFilteredTimezoneOptions = (): MenuProps['items'] => {
    const momentDate = moment(focusDate)
    let timeZoneList = allTimezoneInfo
    if (searchText) {
      timeZoneList = timeZoneList.filter((tzInfo) =>
        tzInfo.searchtext.toLowerCase().includes(searchText.toLowerCase()),
      )
    }
    return timeZoneList
      .map((tzInfo) => {
        return {
          key: pickerId + ':' + tzInfo.tzId,
          offset: tzInfo.tzId ? momentDate.tz(tzInfo.tzId)?.utcOffset() : Number.MIN_SAFE_INTEGER,
          label: (
            <div className={styles.timezoneMenuItem}>
              <div className={styles.timezoneName}>
                {tzInfo.cityName +
                  (tzInfo.tzId
                    ? ' (' + getTZOffsetFromMoment(momentDate.tz(tzInfo.tzId)) + ')'
                    : '')}
              </div>
              <div className={styles.timezoneId}>{tzInfo.tzId}</div>
            </div>
          ),
          onClick: () => {
            onTzChange(tzInfo.tzId)
            setIsOpen(false)
          },
        }
      })
      .sort((a, b) => (a.offset as number) - (b.offset as number))
  }

  const renderTimezoneInput = (tzId: string | null): React.ReactNode => (
    <div className={styles.timezoneInputWrapper}>
      <input
        ref={tzInputRef}
        className={styles.timezoneInput}
        placeholder={getTimezoneOffsetAndName(tzId, focusDate)}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        onBlur={() => {
          setTimeout(() => {
            setSearchText('')
            setIsOpen(false)
          }, 200)
        }}
        onFocus={() => {
          setIsOpen(true)
          setTimeout(() => {
            scrollToSelected()
          }, 100)
        }}
      />
      <img src={chevronDownIcon} alt="More" className={styles.chevronIcon} />
    </div>
  )

  const scrollToSelected = (): void => {
    const selectedItem = document.querySelector(`[data-menu-id$="${pickerId}:${tzId}"]`)
    if (selectedItem) {
      setTimeout(() => {
        selectedItem.scrollIntoView({
          behavior: 'instant',
          block: 'center',
        })
      }, 100)
    }
  }

  return (
    <div
      className={styles.timezonePickers}
      style={{
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
      onClick={(e) => {
        if (tzInputRef.current && !isOpen) {
          tzInputRef.current.focus()
        } else {
          e.stopPropagation()
        }
      }}
    >
      <Dropdown
        open={isOpen}
        menu={{
          items: getFilteredTimezoneOptions(),
          selectedKeys: [pickerId + ':' + tzId],
          style: { maxHeight: '400px', overflow: 'auto' },
        }}
        placement="bottomLeft"
        overlayClassName={styles.timezoneDropdown}
        destroyOnHidden={true}
      >
        {renderTimezoneInput(tzId)}
      </Dropdown>
    </div>
  )
}

export default TimezonePicker
